import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useMyServices, useTechnologies } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import { StatusBadge } from '../shared/StatusBadge';
import type { WidgetConfig } from './types';
import type { Technology } from '../../graphql/types';
import { TechnologyLifecycleStage } from '../../graphql/types';
import '@tylertech/forge/icon';
import '@tylertech/forge/button';
import './TechnologyStatisticsWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'technology-statistics',
  title: 'Technology',
  size: 'medium',
  refreshInterval: 30000,
  showRefresh: true,
  showExpand: false,
};

const EOL_WARNING_MONTHS = 12;

/** The widget's two selectable scopes (issue #491). Persisted in `DashboardWidget.config.scope`. */
export type TechnologyWidgetScope = 'all' | 'my-team';

const SCOPE_LABEL: Record<TechnologyWidgetScope, string> = {
  all: 'All technologies',
  'my-team': "My team's technologies",
};

function isTechnologyWidgetScope(value: unknown): value is TechnologyWidgetScope {
  return value === 'all' || value === 'my-team';
}

/** Reads the widget's scope from its persisted config, defaulting to `'all'` (pre-#491 behavior). */
function getScope(config: Record<string, unknown> | undefined): TechnologyWidgetScope {
  const raw = config?.scope;
  return isTechnologyWidgetScope(raw) ? raw : 'all';
}

export interface TechnologyStatisticsWidgetProps {
  /** This widget's persisted config (`{ scope?: 'all' | 'my-team' }`). */
  config?: Record<string, unknown>;
  /** Called with the widget's full updated config when the scope toggle changes. */
  onConfigChange?: (config: Record<string, unknown>) => void;
}

/** Fixed display order for the four lifecycle stages — always shown, even at 0. */
const LIFECYCLE_STAGE_ORDER: TechnologyLifecycleStage[] = [
  TechnologyLifecycleStage.Adopt,
  TechnologyLifecycleStage.Hold,
  TechnologyLifecycleStage.Evaluate,
  TechnologyLifecycleStage.Sunset,
];

interface TechnologyStats {
  total: number;
  byLifecycleStage: Record<TechnologyLifecycleStage, number>;
  approachingEolCount: number;
}

function isApproachingEol(supportEndDate: string | null | undefined): boolean {
  if (!supportEndDate) return false;
  const eolDate = new Date(supportEndDate);
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() + EOL_WARNING_MONTHS);
  return eolDate <= cutoff && eolDate >= new Date();
}

type TechnologiesData = ReturnType<typeof useTechnologies>['data'];

function extractTechnologyStats(data: TechnologiesData): TechnologyStats | null {
  if (!data?.technologies?.edges) {
    return null;
  }

  const technologies = data.technologies.edges.map((edge) => edge.node);

  const byLifecycleStage: Record<TechnologyLifecycleStage, number> = {
    [TechnologyLifecycleStage.Adopt]: 0,
    [TechnologyLifecycleStage.Hold]: 0,
    [TechnologyLifecycleStage.Evaluate]: 0,
    [TechnologyLifecycleStage.Sunset]: 0,
  };
  let approachingEolCount = 0;

  technologies.forEach((tech: Technology) => {
    byLifecycleStage[tech.lifecycleStage] = (byLifecycleStage[tech.lifecycleStage] || 0) + 1;
    if (isApproachingEol(tech.supportEndDate)) {
      approachingEolCount++;
    }
  });

  return { total: technologies.length, byLifecycleStage, approachingEolCount };
}

export function TechnologyStatisticsWidget({
  config,
  onConfigChange,
}: TechnologyStatisticsWidgetProps = {}): JSX.Element {
  const scope = getScope(config);

  // Only relevant when scope === 'my-team'; cheap/cached when scope === 'all' since
  // Apollo dedupes against MyServicesWidget's identical query.
  const myServicesResult = useMyServices();
  const myServiceIds = useMemo(
    () => (myServicesResult.data?.myServices?.edges ?? []).map((edge) => edge.node.entityId),
    [myServicesResult.data]
  );

  const filter = scope === 'my-team' ? { serviceIds: myServiceIds } : undefined;
  const technologiesResult = useTechnologies(filter, 2000);

  // Gate on myServices loading too while scoped, so the widget shows its loading
  // skeleton instead of a flash of stale/all-technologies data before the
  // service-scoped serviceIds filter is ready.
  const scopeReady = scope !== 'my-team' || !myServicesResult.loading;
  const combinedResult = {
    data: scopeReady ? technologiesResult.data : undefined,
    loading: technologiesResult.loading || (scope === 'my-team' && myServicesResult.loading),
    error: technologiesResult.error,
    refetch: (): unknown => {
      if (scope === 'my-team') {
        myServicesResult.refetch();
      }
      return technologiesResult.refetch();
    },
  };

  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    combinedResult,
    extractTechnologyStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  const handleScopeChange = (next: TechnologyWidgetScope): void => {
    if (next === scope) return;
    onConfigChange?.({ ...config, scope: next });
  };

  const scopeToggle = (
    <div
      className="technology-statistics-widget__scope-toggle"
      role="group"
      aria-label="Technology scope"
    >
      <ForgeButton
        type="button"
        variant={scope === 'all' ? 'raised' : 'outlined'}
        onClick={() => handleScopeChange('all')}
        data-testid="technology-widget-scope-all"
      >
        All
      </ForgeButton>
      <ForgeButton
        type="button"
        variant={scope === 'my-team' ? 'raised' : 'outlined'}
        onClick={() => handleScopeChange('my-team')}
        data-testid="technology-widget-scope-my-team"
      >
        My Team
      </ForgeButton>
    </div>
  );

  const scopeSubtext = (
    <p className="technology-statistics-widget__scope-label forge-typography--caption">
      {/* scope is narrowed by isTechnologyWidgetScope() — not an arbitrary user-controlled key */}
      {/* eslint-disable-next-line security/detect-object-injection */}
      Showing: {SCOPE_LABEL[scope]}
    </p>
  );

  if (!stats || stats.total === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="technology-statistics-widget__scope-row">
          {scopeToggle}
          {scopeSubtext}
        </div>
        <div className="technology-statistics-widget__empty">
          <IconElement
            name="developer_board"
            className="technology-statistics-widget__empty-icon"
          />
          <p className="forge-typography--body2">
            {scope === 'my-team'
              ? "No technologies used by your team's services"
              : 'No technologies available'}
          </p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="technology-statistics-widget__content">
        <div className="technology-statistics-widget__scope-row">
          {scopeToggle}
          {scopeSubtext}
        </div>
        <div className="technology-statistics-widget__stage-grid">
          {LIFECYCLE_STAGE_ORDER.map((stage) => (
            <div key={stage} className="technology-statistics-widget__stage-tile">
              <div className="technology-statistics-widget__stage-count">
                {/* stage is from LIFECYCLE_STAGE_ORDER (TechnologyLifecycleStage enum) — not user-controlled */}
                {/* eslint-disable-next-line security/detect-object-injection */}
                {stats.byLifecycleStage[stage]}
              </div>
              <StatusBadge variant="lifecycleStage" value={stage} />
            </div>
          ))}
        </div>

        {stats.approachingEolCount > 0 && (
          <div className="technology-statistics-widget__eol-warning">
            <IconElement name="warning" className="technology-statistics-widget__eol-icon" />
            <span className="technology-statistics-widget__eol-count forge-typography--label1">
              {stats.approachingEolCount}
            </span>
            <span className="technology-statistics-widget__eol-label forge-typography--body2">
              approaching end-of-support within 12 months
            </span>
          </div>
        )}

        <div className="technology-statistics-widget__footer">
          <Link
            to="/technologies"
            search={{ page: 1, pageSize: 25, supplierId: '', productFamilyId: '' }}
            className="technology-statistics-widget__view-all forge-typography--body2"
          >
            View all →
          </Link>
        </div>
      </div>
    </DashboardWidget>
  );
}
